/**
 * Settings → Version retention → "By folder" (#616).
 *
 * The fourth pill beside By file type / Per-file overrides / App versions, and
 * the one place a tenant says how much history the files under a folder keep.
 *
 * ── AN ORDERED LIST: THE FIRST MATCH FROM THE TOP WINS ────────────────────
 *
 * A rule's value is a COUNT, `1..max`. `.git`, `node_modules` and
 * `__pycache__` are seeded at 1; the kids'-videos case is a folder at 3.
 *
 * The rows are DRAGGABLE and the order is the priority. It resolved by
 * directory depth once — `Videos/Kids` beat `Videos` wherever both matched —
 * and that changed for this screen rather than for the engine: a page that
 * lets you drag rows and then silently overrides your ordering on most of
 * them is worse than one with no drag at all, because the control does
 * nothing and never says why.
 *
 * So ordering always means something, and the case where it means something
 * surprising — a general rule above a specific one, which kills the specific
 * one — is called out per row from the server's `shadowed` list. The fix is
 * a drag, which is the point.
 *
 * Drag is not the only way to reorder. The up/down buttons are the
 * accessible path: HTML5 drag is unreachable from a keyboard, and ordering
 * is now load-bearing rather than cosmetic.
 *
 * There is no `0` and no "off". A file that exists IS version 1, and a folder
 * rule never stops a folder being synced, listed or charged for — it only
 * bounds what future writes prune. An earlier shape of this tab had a fourth
 * state that DID stop the walk, and removing it is what let this screen lose
 * its preview and its reclaim panel: neither had anything left to warn about.
 * `FolderRetention` in `@papan/types` records why the state went.
 *
 * That is also why saving here is ordinary. The old flow disarmed Save until
 * the exact staged list had been previewed, because a mistyped pattern could
 * silently stop a folder syncing and the user would find out weeks later
 * looking for a version that was never written. A mistyped pattern now means
 * a folder keeps the tenant's default number of versions instead of the one
 * you meant — visible, reversible, and not worth a tenant-sized scan before
 * every save.
 *
 * ── THE DEFAULT ROWS ARE ORDINARY ROWS ────────────────────────────────────
 *
 * The three seeded names arrive from the server as a SEED, not as a floor and
 * not as a merge. They can be renamed, re-numbered or deleted like anything
 * else, because a row a user can see and edit but not remove is a worse
 * surface than no row at all. The screen marks which came from the seed and
 * says so when one is renamed, which is the honest version of what the old
 * "built-in, cannot be removed" label was doing.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useRefreshWhenShown, DirtyBar, Tooltip, Modal, useDraftState, RetentionPurgeDialog, RetentionPurgeReceipt, type RetentionPurgeOutcomeView } from '@papan/core-ui/ui'
import {
  normalizePattern, MAX_VERSION_RETENTION, SEEDED_DIR_RETENTION,
  type PatternRejection,
} from '@papan/types'
import {
  syncExclusionsApi,
  type SyncExclusionPurgePreview,
  type SyncExclusionRule,
  type SyncExclusionsResponse,
} from '../../client/storage'
import { Section, formatBytes } from '../budget/bits'
import {
  TrashGlyph, PencilGlyph, InfoGlyph, GripGlyph, ChevronUpGlyph, ChevronDownGlyph,
  InlineRetentionField, parseField,
} from './shared'

/**
 * One row as the screen holds it.
 *
 * `seeded` is the pattern this row ARRIVED as, or `null` for a row the user
 * added. It is what lets the screen say "this is one of the defaults, and you
 * have renamed it" without the server having to track identity — a rule has no
 * id, and inventing one would make the stored shape carry UI state.
 */
interface Row {
  key: string
  pattern: string
  versions: number
  seeded: string | null
}

/**
 * The space the dragged row will land in.
 *
 * A real element in the flow rather than a transform on its neighbours: these
 * rows are not a fixed height — one grows a warning line under it — so the
 * app rail's trick of translating every tile by a constant slot would shift
 * by the wrong amount the moment any rule is invalid or shadowed. Letting the
 * browser measure a placeholder is right whatever the rows contain.
 *
 * Sized to a bare row (`py-2.5` on a `text-sm` line) so the list does not
 * jump when the gap opens next to a one-line rule, which is the common case.
 */
function GapPlaceholder() {
  return (
    <div
      data-testid="folder-rules-gap"
      aria-hidden="true"
      // TRANSPARENT TO THE POINTER, or it eats the gesture that summoned it.
      // Opening the gap above a row pushes that row DOWN, so the placeholder
      // lands exactly where the cursor already is — and a plain div is not a
      // drop target, so `dragover` stopped reaching anything that calls
      // `preventDefault` and the browser refused the drop.
      className="rounded-lg border border-dashed border-blue-500/50 bg-blue-500/5 h-[38px] pointer-events-none"
    />
  )
}

/** Rows → the rules to store. The stored list is AUTHORITATIVE — there is no
 *  merge under it — so this is simply every row, in order. */
const toRules = (rows: Row[]): SyncExclusionRule[] =>
  rows.map((r) => ({ pattern: r.pattern.trim(), versions: r.versions }))

/** Server state → rows. */
const EMPTY_ROWS: Row[] = []

const toRows = (data: SyncExclusionsResponse): Row[] => {
  const defaults = new Set(data.defaults.map((d) => d.pattern))
  return data.rules.map((r, i) => ({
    key: `r${i}:${r.pattern}`,
    pattern: r.pattern,
    versions: r.versions,
    seeded: defaults.has(r.pattern) ? r.pattern : null,
  }))
}

/** Do two rule lists say the same thing? Order-sensitive on purpose: the
 *  server stores and echoes an ordered list, and comparing sets would call two
 *  different stored lists equal. */
const sameRules = (a: SyncExclusionRule[], b: SyncExclusionRule[]): boolean =>
  a.length === b.length && a.every((r, i) => r.pattern === b[i]!.pattern && r.versions === b[i]!.versions)

export function FolderRulesTab({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation('core-ui')

  const [data, setData] = useState<SyncExclusionsResponse | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // #897 — the edited rule list is a draft over the server's rules (kept across a
  // phone/desktop layout swap; this tab is desktop-only, so it comes back when
  // the window widens again). Shown live from `data` until edited.
  const seededRows = useMemo(() => (data ? toRows(data) : EMPTY_ROWS), [data])
  const [rows, setRows, { clear: clearRows }] = useDraftState<Row[]>(`folderRules:${tenantId}:rows`, seededRows, {
    // "Unchanged" as `dirty` reads it — the same rules, whatever the row keys:
    // such a list is no draft, so later reloads still show through (#897).
    isSame: (a, b) => sameRules(toRules(a), toRules(b)),
  })
  const [busy, setBusy] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const [infoOpen, setInfoOpen] = useState(false)

  /** Only one count field on the tab is ever `editing` at a time — the same
   *  contract `InlineRetentionField` has on the By-file-type tab, where
   *  clicking a second pencil silently abandons the first. */
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')

  /**
   * The row whose NAME is being edited, and the draft text.
   *
   * Separate from `editingKey` (the count field) on purpose: they are two
   * different editors on one row and opening one must not close the other
   * silently, which a single "what is open" key would do.
   */
  const [renamingKey, setRenamingKey] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const renameRef = useRef<HTMLInputElement>(null)

  /**
   * Drag-reorder. Plain HTML5 DnD — no library: one list, one axis, and the
   * up/down buttons are the accessible path, because drag alone would make
   * ordering keyboard-unreachable and ordering is what decides which rule
   * wins.
   *
   * ── THE LIST OPENS A GAP, THE WAY THE APP RAIL DOES ──────────────────────
   *
   * `gap` is the index the dragged row would land at IN THE LIST WITHOUT IT,
   * and the render inserts a placeholder there. That is the whole mechanism:
   * the placeholder takes a row's worth of space, so everything below it
   * moves down on its own, and it doubles as the marker showing where the
   * drop lands.
   *
   * The rail (`TaskBar`/`useRailDrag`) gets the same effect by translating
   * tiles a fixed `RAIL_SLOT` each, which it can do because every tile is the
   * same height. These rows are not — a row grows a warning line under it —
   * so a fixed translate would shift by the wrong amount as soon as any rule
   * is invalid or shadowed. A real element in the flow is measured by the
   * browser and is right whatever the rows contain.
   *
   * The source row collapses rather than unmounting. Removing the node under
   * a live HTML5 drag ends the gesture in some browsers, and `dragend` is
   * what clears this state.
   */
  const dragFrom = useRef<number | null>(null)
  /** WHICH row is being dragged. Set synchronously — the gap arithmetic needs
   *  it from the first `dragover`, and rendering it changes no box. */
  const [dragging, setDragging] = useState<number | null>(null)
  /** WHETHER that row has collapsed yet. A frame behind `dragging`, and the
   *  only thing that touches its geometry — see the `dragstart` handler. */
  const [collapsed, setCollapsed] = useState(false)
  /** The purge the pending save would perform, once previewed. Non-null IS
   *  the dialog being open. */
  const [confirm, setConfirm] = useState<SyncExclusionPurgePreview | null>(null)
  /** What the last save actually removed, for the receipt under the list. */
  /** What the purge ACTUALLY removed (app versions included), or that it went to the background job (#1443). */
  const [purged, setPurged] = useState<RetentionPurgeOutcomeView | null>(null)
  const [gap, setGapState] = useState<number | null>(null)
  /** The same value for the DROP to read. `drop` fires on whatever the pointer
   *  is over, which is not always the element whose render produced `gap`. */
  const gapRef = useRef<number | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const setGap = (v: number | null) => { gapRef.current = v; setGapState(v) }

  const endDrag = () => {
    dragFrom.current = null
    setDragging(null)
    setCollapsed(false)
    setGap(null)
  }

  const load = useCallback(() => {
    syncExclusionsApi.get(tenantId)
      .then((d) => { setData(d); setLoadError(null) })
      .catch((e: any) => setLoadError(e?.message || t('settings.folderRules.loadFailed')))
    // `tenantId` only — not `t`. Same rule as the page around it: this
    // refetches when the TENANT changes, not when the translator re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId])

  useEffect(() => { load() }, [load])

  // Focus and select on open, so the gesture is double-click-then-type. Same
  // as the workspace menu bar's rename.
  useEffect(() => {
    if (renamingKey && renameRef.current) {
      renameRef.current.focus()
      renameRef.current.select()
    }
  }, [renamingKey])

  const staged = useMemo(() => toRules(rows), [rows])
  const savedRules = data?.rules ?? []
  const dirty = !sameRules(staged, savedRules)

  // Keep-alive decks do not remount, so a mount-once fetch goes permanently
  // stale on re-show. WITHHELD while edits are staged — a refresh that reset
  // the list under a half-made change would discard work the user can see.
  // Inert with no DeckVisibility provider (mobile/tests).
  useRefreshWhenShown(() => { if (!dirty) load() })

  /** Every row's pattern, validated with the ROUTE'S OWN function, so a bad
   *  rule is refused as the user types rather than after a round trip. */
  const problems = useMemo(() => {
    const seen = new Map<string, number>()
    const out = new Map<string, PatternRejection | 'duplicate'>()
    rows.forEach((r) => {
      const n = normalizePattern(r.pattern)
      if (!n.ok) { out.set(r.key, n.reason); return }
      const priorCount = seen.get(n.value)
      if (priorCount != null) out.set(r.key, 'duplicate')
      seen.set(n.value, (priorCount ?? 0) + 1)
    })
    return out
  }, [rows])

  const atLimit = data != null && rows.length >= data.limits.maxPatterns

  /**
   * Which rows the SERVER says can never fire, keyed by pattern.
   *
   * Server-computed, not recomputed here: a second implementation of "does
   * this rule cover that one" in the browser is how a screen ends up warning
   * about a rule that fires. It is therefore for the SAVED list, and goes
   * stale the moment a row is dragged — which is honest, because the answer
   * for the staged list is not known until it is saved. A warning that
   * guessed would be worse than one that waits.
   */
  const shadowOf = useMemo(
    () => new Map((data?.shadowed ?? []).map((sh) => [sh.pattern, sh.shadowedBy])),
    [data],
  )

  const mutate = (next: (prev: Row[]) => Row[]) => {
    setRows(next)
    setSaveError(null)
  }

  const setPattern = (key: string, pattern: string) =>
    mutate((prev) => prev.map((r) => (r.key === key ? { ...r, pattern } : r)))

  const setVersions = (key: string, versions: number) =>
    mutate((prev) => prev.map((r) => (r.key === key ? { ...r, versions } : r)))

  const removeRow = (key: string) => {
    mutate((prev) => prev.filter((r) => r.key !== key))
    if (editingKey === key) setEditingKey(null)
    if (renamingKey === key) setRenamingKey(null)
  }

  /** Move a row to a new index. Reorders in place — the array IS the
   *  priority, so there is no position field to renumber. */
  const move = (from: number, to: number) => {
    if (to < 0 || to >= rows.length || from === to) return
    mutate((prev) => {
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved!)
      return next
    })
  }

  const beginRename = (r: Row) => {
    setRenameValue(r.pattern)
    setRenamingKey(r.key)
  }

  const commitRename = (r: Row) => {
    setRenamingKey(null)
    const trimmed = renameValue.trim()
    // An empty box is a cancel, not a rename to "". A blank pattern is
    // refused by `normalizePattern` anyway, so committing one would only put
    // the row into an error state the user did not ask for.
    if (trimmed && trimmed !== r.pattern) setPattern(r.key, trimmed)
  }

  const addRow = () => {
    if (atLimit) return
    // A new row starts at the seeded count, which is also the lowest legal
    // one. There is no "inherit" to start from — a folder rule that inherits
    // is just no rule, which is what NOT adding the row already expresses.
    const key = `new${Date.now()}`
    mutate((prev) => [
      ...prev,
      { key, pattern: '', versions: SEEDED_DIR_RETENTION, seeded: null },
    ])
    // Open the editor on it straight away. The name is plain text at rest, so
    // a new row would otherwise appear as a greyed-out example with no
    // indication that the next step is to type over it — the user would have
    // to discover the double-click (or the pencil) before they could name the
    // thing they just asked to create.
    setRenameValue('')
    setRenamingKey(key)
  }

  const beginEdit = (r: Row) => {
    setEditingKey(r.key)
    setEditValue(String(r.versions))
  }

  const confirmEdit = (r: Row) => {
    const parsed = parseField(editValue)
    // A blank box is `null` from `parseField`, which means "inherit" on the
    // tabs that share this field and means nothing here. Refuse it rather
    // than inventing a value: the row keeps what it had and the field stays
    // open, so the user sees their edit did not take.
    if (!parsed.valid || parsed.value == null) return
    setVersions(r.key, parsed.value)
    setEditingKey(null)
  }

  /**
   * Write the list, optionally purging the history the new caps exclude.
   *
   * `purge` is only ever `true` on the far side of the confirm dialog — the
   * server does nothing destructive without it, so a bug on this screen cannot
   * delete a version the user was not shown a number for.
   */
  const commit = async (purge: boolean) => {
    setBusy(true)
    setSaveError(null)
    try {
      // The response echoes the NORMALISED, de-duplicated list the server
      // stored. Rendering what was SENT instead would let the screen disagree
      // with the engine from the save onwards.
      const res = await syncExclusionsApi.set(tenantId, staged.length === 0 ? null : staged, { purge })
      const next = { ...(data as SyncExclusionsResponse), rules: res.rules, shadowed: res.shadowed }
      setData(next)
      clearRows()
      // What was ACTUALLY removed, which the preview could only bound from
      // above. Reported rather than assumed — a file whose versions are pinned
      // by an app snapshot keeps them, and saying "12 removed" when 9 were is
      // the kind of small lie that makes the whole number untrustworthy.
      setPurged(res.purged || res.queued ? { purged: res.purged, queued: res.queued } : null)
    } catch (e: any) {
      setSaveError(e?.message || t('settings.folderRules.saveFailed'))
    } finally {
      setBusy(false)
      setConfirm(null)
    }
  }

  /**
   * The Save button. Asks what the save would destroy BEFORE writing anything.
   *
   * Most saves destroy nothing and go straight through — adding a rule,
   * raising one, renaming one. Only a genuine reduction reaches the dialog,
   * and the server re-decides that for itself rather than trusting this.
   *
   * A preview that FAILS does not block the save; it falls back to the
   * non-destructive write. Refusing to save because we could not count is the
   * wrong trade — the user still gets their rules, they just do not get the
   * purge, and the next lowering will offer it again.
   */
  const save = async () => {
    setBusy(true)
    setSaveError(null)
    setPurged(null)
    let preview: SyncExclusionPurgePreview | null = null
    try {
      preview = await syncExclusionsApi.purgePreview(tenantId, staged)
    } catch {
      preview = null
    }
    setBusy(false)
    if (preview?.willPurge) { setConfirm(preview); return }
    await commit(false)
  }

  const discard = () => {
    if (!data) return
    clearRows()
    setEditingKey(null)
    setRenamingKey(null)
    setSaveError(null)
  }

  if (loadError) return <p className="text-xs text-red-400">{loadError}</p>
  if (!data) return <p className="themed-text-muted text-xs">{t('common:loading')}</p>

  return (
    <div
      data-testid="folder-rules"
      // THE WHOLE TAB ACCEPTS THE DROP, not just the rows.
      //
      // A drop only commits where something called `preventDefault` on the
      // `dragover` under the cursor, and the cursor spends much of a drag over
      // neither row nor list: the gutter between two rows, the placeholder,
      // and — the reported case — anywhere BELOW the last row, which is
      // outside the list element entirely. Handling it here means the drop
      // lands wherever the user let go, and always at the gap they can see.
      onDragOver={(e) => {
        if (dragFrom.current === null) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        // A row that the pointer is actually over has already placed the gap
        // against its own midpoint, which is finer than anything measurable
        // from out here. Only resolve the cases it cannot see.
        const target = e.target as HTMLElement | null
        if (target?.closest('[data-testid^="folder-rules-row-"]')) return
        const list = listRef.current
        if (!list) return
        const box = list.getBoundingClientRect()
        // Past the end of the list, or before its start. In between, the
        // cursor is in a gutter or over the placeholder — the gap is already
        // where it belongs, and moving it would make it flicker.
        if (e.clientY > box.bottom) setGap(rows.length - 1)
        else if (e.clientY < box.top) setGap(0)
      }}
      // The ONE commit point. Row drops bubble to here, so there is no second
      // handler that could double-apply the move or disagree about the index.
      onDrop={(e) => {
        e.preventDefault()
        if (dragFrom.current !== null && gapRef.current !== null) move(dragFrom.current, gapRef.current)
        endDrag()
      }}
      className="flex flex-col gap-3"
    >
      {/* The reassurance stays in the open, not behind the `i`: "will this
          throw my work away" is the question a user arrives with, and it has
          to be answered before they touch anything.

          It used to answer "nothing is deleted", flatly. That stopped being
          true when lowering a rule started purging, and a reassurance that has
          quietly become false is worse than no reassurance — so it now says
          what the rule does to existing history and that a deletion is always
          confirmed first. What the `i` holds is the part you only need once —
          what a count DOES, and the pattern syntax. */}
      <div className="flex items-start gap-2">
        <p className="themed-text-muted text-xs max-w-full sm:max-w-[75%]">
          {t('settings.folderRules.keepNotice')}
        </p>
        <Tooltip content={t('settings.folderRules.info.open')} always placement="top" className="inline-flex shrink-0">
          <button
            type="button"
            data-testid="folder-rules-info-open"
            onClick={() => setInfoOpen(true)}
            aria-label={t('settings.folderRules.info.open')}
            className="themed-text-muted hover:themed-text shrink-0 w-6 h-6 flex items-center justify-center outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500/50"
          >
            <InfoGlyph size={14} />
          </button>
        </Tooltip>
      </div>

      {infoOpen && (
        <Modal
          title={t('settings.folderRules.info.title')}
          onClose={() => setInfoOpen(false)}
          maxWidth="max-w-xl"
          footer={(
            <button
              type="button"
              onClick={() => setInfoOpen(false)}
              className="px-2.5 py-1 text-xs rounded themed-btn-secondary"
            >
              {t('settings.folderRules.info.close')}
            </button>
          )}
        >
          {/* `Modal` pads its header and footer but NOT its children — each
              caller pads its own body, so that a dialog can run a full-bleed
              table or image when it wants to. Matching the header's `px-5`
              keeps the body aligned with the title above it. */}
          <div data-testid="folder-rules-info" className="px-5 py-4 flex flex-col gap-4 text-xs">
            <div className="flex flex-col gap-1">
              <div className="themed-text font-medium">{t('settings.folderRules.info.keptTitle')}</div>
              <p className="themed-text-muted">{t('settings.folderRules.info.keptBody')}</p>
            </div>
            {/* The thing a low number does NOT buy. Someone setting
                `node_modules` to 1 expecting their syncs to get faster has
                misread the screen, and this is where they find out. */}
            <div className="flex flex-col gap-1">
              <div className="themed-text font-medium">{t('settings.folderRules.info.costTitle')}</div>
              <p className="themed-text-muted">{t('settings.folderRules.info.costBody')}</p>
            </div>

            {/* The syntax. Nothing else on the screen says a glob is allowed,
                and a user who does not know will never type one. */}
            <div className="flex flex-col gap-1.5">
              <div className="themed-text font-medium">{t('settings.folderRules.info.patternsTitle')}</div>
              {/* Rows stack below `sm`: at phone width the fixed example column
                  leaves too little for the sentence beside it, and a
                  two-word-per-line description is harder to read than a
                  stacked pair. `min-w-0` so a long line wraps rather than
                  pushing the row past the panel. */}
              <dl className="flex flex-col gap-1">
                {(['name', 'glob', 'rooted', 'file', 'deep'] as const).map((k) => (
                  <div key={k} className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3">
                    <dt className="font-mono themed-text shrink-0 sm:w-28">
                      {t(`settings.folderRules.info.pattern.${k}.example`)}
                    </dt>
                    <dd className="themed-text-muted m-0 min-w-0">
                      {t(`settings.folderRules.info.pattern.${k}.means`)}
                    </dd>
                  </div>
                ))}
              </dl>
              <p className="themed-text-muted">{t('settings.folderRules.info.patternsNote')}</p>
            </div>
          </div>
        </Modal>
      )}

      {/* THE PURGE CONFIRMATION.

          Lowering a rule deletes history that already exists, which is the one
          irreversible thing this screen can do. It is reached only through a
          server-side preview, it names what it will destroy, and its primary
          action is the destructive one so nobody confirms it by reflex on the
          Enter key.

          TWO choices, not three. There was a "save but keep the versions"
          option, on the reasoning that refusing the deletion should not cost
          the user their rule change. It was wrong: it leaves the tenant with a
          rule saying "keep 1" over files holding fifty, so the screen states
          one thing and the drive holds another until each file is next
          written. Either the rule and the history agree, or nothing is saved
          and the staged edit is still there to change. */}
      {confirm && (
        // The shared "Delete these versions?" dialog (#1443) — the purge's own numbers, the rule behind each file.
        <RetentionPurgeDialog
          preview={confirm}
          busy={busy}
          onCancel={() => setConfirm(null)}
          onConfirm={() => void commit(true)}
          testId="folder-rules-purge"
          formatBytes={formatBytes}
        />
      )}

      {/* The ordering rule, said once above the list. It is the one thing a
          reader cannot infer from the rows: drag handles say "you can
          reorder", not "the order decides which rule wins". */}
      {rows.length > 1 && (
        <p data-testid="folder-rules-order" className="themed-text-muted text-[10px] max-w-full sm:max-w-[75%]">
          {t('settings.folderRules.order')}
        </p>
      )}

      {rows.length === 0 && (
        <p data-testid="folder-rules-empty" className="themed-text-muted text-xs">
          {t('settings.folderRules.empty')}
        </p>
      )}

      {/* One card per rule — the By-file-type idiom, because these are the
          same kind of thing: a named subject with a retention value beside
          it. The row geometry (`px-3 py-2.5`, `gap-2`, a 15px leading glyph
          and a `text-sm font-medium` name) is that tab's, so the two read as
          one screen rather than two. */}
      <div ref={listRef} data-testid="folder-rules-list" className="flex flex-col gap-2">
        {rows.map((r, i) => {
          const problem = problems.get(r.key)
          const renamed = r.seeded !== null && r.seeded !== r.pattern
          const shadowedBy = shadowOf.get(r.pattern)
          const renaming = renamingKey === r.key
          const isSource = dragging === i
          // Where this row sits once the dragged one is lifted out. The gap is
          // an index into THAT list, so the placeholder can only be compared
          // against these.
          const restIndex = dragging !== null && i > dragging ? i - 1 : i
          return (
            <Fragment key={r.key}>
              {gap === restIndex && !isSource && <GapPlaceholder />}
            <div
              data-pattern={r.pattern}
              data-testid={`folder-rules-row-${r.key}`}
              // THE WHOLE ROW IS THE HANDLE, not the grip. The grip is a
              // mark saying "this moves"; requiring the pointer to be on it
              // makes a 14px target out of a 600px row for no benefit.
              //
              // Not draggable while EITHER text box is open — the name or the
              // count. The browser hands a drag the whole row, so a selection
              // gesture inside an input would drag the rule instead of
              // selecting the text in it.
              draggable={!renaming && editingKey !== r.key && !busy}
              onDragStart={(e) => {
                dragFrom.current = i
                // `dataTransfer` FIRST, and the collapse LAST — see below.
                // Without a payload the drop handler never runs in Firefox.
                e.dataTransfer.effectAllowed = 'move'
                try { e.dataTransfer.setData('text/plain', r.pattern) } catch { /* Safari */ }
                // Which row is moving: needed synchronously, because the
                // first `dragover` does arithmetic against it. Rendering it
                // changes nothing about this row's box.
                setDragging(i)
                // COLLAPSING IT IS DEFERRED A FRAME, AND MUST STAY THAT WAY.
                //
                // React flushes a discrete event synchronously, so collapsing
                // here re-rendered this row to `h-0` BEFORE the browser had
                // captured its drag image — and Chrome cancels a drag whose
                // source goes zero-sized during `dragstart`. The gesture did
                // not degrade, it never started: no dragover, no drop,
                // nothing. A frame later the image is taken and the element
                // is free to change.
                requestAnimationFrame(() => setCollapsed(true))
              }}
              onDragOver={(e) => {
                const from = dragFrom.current
                if (from === null) return
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                // Top half of the row means "land above me", bottom half
                // "below me". Measuring the pointer against the row's own box
                // is what makes the gap follow the cursor rather than jump a
                // whole row at the boundary.
                const box = e.currentTarget.getBoundingClientRect()
                const below = e.clientY > box.top + box.height / 2
                const target = i + (below ? 1 : 0)
                // Convert to an index in the list WITHOUT the dragged row.
                setGap(from < target ? target - 1 : target)
              }}
              // No `onDrop` here: it lives on the tab container, which sees
              // this row's drop by bubbling AND the drops that never reach a
              // row at all.
              onDragEnd={endDrag}
              // The dragged row COLLAPSES rather than staying put: the browser
              // is already drawing a drag image under the cursor, so leaving
              // the original in place would show the rule twice AND grow the
              // list by a row — the gap opens elsewhere, so the total height
              // only stays put if this one gives its space back.
              //
              // Collapsed, not unmounted: removing the node under a live HTML5
              // drag ends the gesture in some browsers, and `dragend` is what
              // clears the drag state.
              className={`themed-bg-secondary rounded-lg flex flex-col gap-1 ${
                isSource && collapsed
                  ? 'h-0 py-0 opacity-0 overflow-hidden pointer-events-none'
                  : 'px-3 py-2.5'
              } ${
                // The affordance follows the behaviour: the row is what
                // drags, so the row is what says so.
                !renaming && editingKey !== r.key && !busy
                  ? 'cursor-grab active:cursor-grabbing'
                  : ''
              }`}
            >
              <div className="flex items-center gap-2">
                {/* The grab affordance, and the leading gutter the By-file-type
                    rows get from their chevron column. */}
                <span
                  aria-hidden="true"
                  data-testid={`folder-rules-grip-${r.key}`}
                  className="shrink-0 themed-text-muted"
                >
                  <GripGlyph size={14} />
                </span>
                {/* The name is TEXT until asked for. Double-click opens the
                    editor — the same gesture that renames a project in the
                    workspace menu bar. A permanent input reads as a form to
                    fill in; this is a list you occasionally correct. */}
                {renaming ? (
                  <input
                    ref={renameRef}
                    value={renameValue}
                    maxLength={data.limits.maxPatternLength}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => commitRename(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commitRename(r) }
                      else if (e.key === 'Escape') { e.preventDefault(); setRenamingKey(null) }
                    }}
                    aria-label={t('settings.folderRules.patternAria')}
                    // An example of the SYNTAX, not of a folder name. A new row
                    // opens straight into this box, so it is the only place the
                    // screen can show what a pattern looks like at the moment
                    // someone is about to write one.
                    placeholder={t('settings.folderRules.patternPlaceholder')}
                    data-testid={`folder-rules-pattern-${r.key}`}
                    // The project title's editor, but stretched: that one sits
                    // in a menu bar next to other controls and is capped at
                    // 240px for it. A settings row has the width to spare, and
                    // a folder pattern is the longest thing on the line.
                    className={`text-sm font-medium bg-transparent border rounded px-1.5 py-0 themed-text outline-none focus:border-brand-500 flex-1 min-w-0 ${
                      problem ? 'border-red-500' : 'themed-border'
                    }`}
                  />
                ) : (
                  <span
                    onDoubleClick={() => { if (!busy) beginRename(r) }}
                    role="button"
                    tabIndex={0}
                    // Enter is the keyboard equivalent of the double-click —
                    // the gesture is otherwise unreachable without a mouse.
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !busy) { e.preventDefault(); beginRename(r) }
                    }}
                    aria-label={t('settings.folderRules.renameAria', { pattern: r.pattern })}
                    data-testid={`folder-rules-name-${r.key}`}
                    title={t('settings.folderRules.renameHint')}
                    // The project title at rest, to the class. No hover fill
                    // and no text cursor: both were promising a text field,
                    // and the name is not one until it is asked for.
                    // `flex-1 min-w-0` rather than a fixed cap: the name takes
                    // the whole line and pushes the count and the bin to the
                    // right edge, which is the By-file-type row's shape. `min-w-0`
                    // is what lets `truncate` fire — a flex item's default
                    // `min-width: auto` refuses to shrink below its text and the
                    // row would widen instead of ellipsing.
                    className={`text-sm font-medium cursor-default select-none truncate flex-1 min-w-0 outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500/50 ${
                      problem ? 'text-red-400' : 'themed-text'
                    }`}
                  >
                    {r.pattern || t('settings.folderRules.patternPlaceholder')}
                  </span>
                )}

                {/* The rename affordance. Double-click still works and is the
                    faster gesture, but it is INVISIBLE — nothing on the row
                    said the name could be edited, so the only people who found
                    it were the ones who already knew. The pencil opens the same
                    editor.

                    Hidden while renaming: the control's own target is now a
                    text box, and a button that re-opens what is already open
                    reads as a second, different action. */}
                {!renaming && (
                  <Tooltip content={t('settings.folderRules.rename')} always placement="top" className="inline-flex shrink-0">
                    <button
                      type="button"
                      onClick={() => { if (!busy) beginRename(r) }}
                      disabled={busy}
                      aria-label={t('settings.folderRules.renameAria', { pattern: r.pattern })}
                      data-testid={`folder-rules-rename-${r.key}`}
                      className="themed-text-muted hover:themed-text disabled:opacity-40 shrink-0 w-6 h-6 flex items-center justify-center outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500/50"
                    >
                      <PencilGlyph size={13} />
                    </button>
                  </Tooltip>
                )}

                {/* Reorder, for anyone not using a mouse. Disabled at the ends
                    rather than hidden, so the column does not reflow. */}
                <Tooltip content={t('settings.folderRules.moveUp')} always placement="top" className="inline-flex shrink-0">
                  <button
                    type="button"
                    onClick={() => move(i, i - 1)}
                    disabled={i === 0 || busy}
                    aria-label={t('settings.folderRules.moveUpAria', { pattern: r.pattern })}
                    data-testid={`folder-rules-up-${r.key}`}
                    className="themed-text-muted hover:themed-text disabled:opacity-30 shrink-0 w-5 h-6 flex items-center justify-center outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500/50"
                  >
                    <ChevronUpGlyph size={13} />
                  </button>
                </Tooltip>
                <Tooltip content={t('settings.folderRules.moveDown')} always placement="top" className="inline-flex shrink-0">
                  <button
                    type="button"
                    onClick={() => move(i, i + 1)}
                    disabled={i === rows.length - 1 || busy}
                    aria-label={t('settings.folderRules.moveDownAria', { pattern: r.pattern })}
                    data-testid={`folder-rules-down-${r.key}`}
                    className="themed-text-muted hover:themed-text disabled:opacity-30 shrink-0 w-5 h-6 flex items-center justify-center outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500/50"
                  >
                    <ChevronDownGlyph size={13} />
                  </button>
                </Tooltip>

                <InlineRetentionField
                  id={`folder-${r.key}`}
                  value={r.versions}
                  inheritedPlaceholder={MAX_VERSION_RETENTION}
                  editing={editingKey === r.key}
                  saving={busy}
                  editValue={editValue}
                  onEditValueChange={setEditValue}
                  onEdit={() => beginEdit(r)}
                  onCancel={() => setEditingKey(null)}
                  onConfirm={() => confirmEdit(r)}
                  // Reset means "back to the seeded count", the value a new row
                  // starts at and the one the defaults ship with. There is no
                  // inherited value for it to mean instead.
                  onReset={() => { setVersions(r.key, SEEDED_DIR_RETENTION); setEditingKey(null) }}
                  label={t('settings.folderRules.versionsAria', { pattern: r.pattern })}
                />
                <Tooltip content={t('settings.folderRules.remove')} always placement="top" className="inline-flex shrink-0">
                  <button
                    type="button"
                    onClick={() => removeRow(r.key)}
                    disabled={busy}
                    aria-label={t('settings.folderRules.removeAria', { pattern: r.pattern })}
                    data-testid={`folder-rules-remove-${r.key}`}
                    className="text-red-500/75 hover:text-red-500 disabled:opacity-40 shrink-0 w-6 h-6 flex items-center justify-center outline-none rounded focus-visible:ring-2 focus-visible:ring-blue-500/50"
                  >
                    <TrashGlyph size={13} />
                  </button>
                </Tooltip>
              </div>

              {/* Per-row copy, in the order a reader needs it: what is wrong,
                  then what is inert, then what is merely notable. */}
              {problem && (
                <p data-testid={`folder-rules-reason-${r.key}`} className="text-[10px] text-red-400">
                  {problem === 'duplicate'
                    ? t('settings.folderRules.duplicate')
                    : t(`settings.folderRules.reason.${problem}`, { max: MAX_VERSION_RETENTION })}
                </p>
              )}
              {shadowedBy != null && (
                <p data-testid={`folder-rules-shadowed-${r.pattern}`} className="text-[10px] text-amber-400">
                  {t('settings.folderRules.shadowed', { pattern: r.pattern, by: shadowedBy })}
                </p>
              )}
              {renamed && (
                <p data-testid={`folder-rules-renamed-${r.key}`} className="text-[10px] text-amber-400">
                  {t('settings.folderRules.renamed', { from: r.seeded })}
                </p>
              )}
            </div>
            </Fragment>
          )
        })}
        {/* The gap can also be PAST the last row — dragging something to the
            bottom. Nothing follows it there, so it has no row to render
            before and needs its own case. */}
        {gap !== null && dragging !== null && gap === rows.length - 1 && <GapPlaceholder />}
      </div>

      {/* The receipt, under the list. Reports what the server ACTUALLY removed,
          which the preview could only bound from above — a version pinned by an
          app snapshot survives, and a number that claimed otherwise would make
          every future number here suspect. */}
      <RetentionPurgeReceipt outcome={purged} testId="folder-rules-purged" />


      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="folder-rules-add"
          onClick={addRow}
          disabled={atLimit || busy}
          className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
        >
          {t('settings.folderRules.add')}
        </button>
        {atLimit && (
          <span className="text-xs text-amber-400">
            {t('settings.folderRules.atLimit', { max: data.limits.maxPatterns })}
          </span>
        )}
      </div>

      <p className="themed-text-muted text-[10px] max-w-full sm:max-w-[75%]">{t('settings.folderRules.hint')}</p>

      {saveError && <p className="text-xs text-red-400">{saveError}</p>}

      <DirtyBar
        count={dirty ? 1 : 0}
        busy={busy}
        // SEPARATE from `busy` on purpose: an invalid list must not disable
        // Discard, which is precisely the state a user most wants a way out of.
        saveDisabled={problems.size > 0}
        onSave={() => void save()}
        onDiscard={discard}
        pendingLabel={t('settings.folderRules.pending')}
        saveLabel={t('settings.folderRules.save')}
        savingLabel={t('settings.folderRules.saving')}
        discardLabel={t('settings.folderRules.discard')}
      />
    </div>
  )
}

/** Kept so a host with no tablist (mobile renders real routes) can still put
 *  the tab inside a titled section. */
export function FolderRulesSection({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation('core-ui')
  return (
    <Section title={t('settings.folderRules.title')} desc={t('settings.folderRules.desc')}>
      <FolderRulesTab tenantId={tenantId} />
    </Section>
  )
}
